import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { MessageService } from 'primeng/api';
import { Store } from '@ngxs/store';
import { take } from 'rxjs/operators';
import { forkJoin } from 'rxjs';

import { CourseLevelEnum, CourseLevelEnumDescriptions } from '../../../models/enums/courseLevelEnum';
import { Gender, GenderDescriptions } from '../../../models/enums/enums';
import { countryResponse } from '../../../models/country/countryResponse';
import { UtilityService } from '../../../services/utility/utility.service';
import { AccountCentreService } from '../../../account-centre/account-centre.service';
import { PersonalInfoState } from '../../../models/enums/peronsalInfoState';
import { AuthService } from '../../../services/account/auth.service';
import { UpdateUser } from '../../../states/user/user-action';
import { UserSelectors } from '../../../states/user/user-selector';
import { Prediction } from '../../../models/country/olaPlacesResponse';

@Component({
  selector: 'app-personal-info',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    CalendarModule,
    DropdownModule,
    MultiSelectModule,
    AutoCompleteModule
  ],
  templateUrl: './personal-info.component.html',
  styleUrls: ['./personal-info.component.scss']
})
export class PersonalInfoComponent implements OnInit {
  @Input() userId: string | undefined;
  @Input() userState: PersonalInfoState | undefined;
  @Input() visible: boolean = false;
  @Output() close = new EventEmitter<void>();
  @Output() updateSuccess = new EventEmitter<void>();
  @Output() showParentsInfo = new EventEmitter<void>();

  personalInfoForm!: FormGroup;

  // Form data
  countries = [
    { label: 'India', value: 'IN', icon: '../../../assets/icons/india-flag.svg', id: 'india' },
    { label: 'UK', value: 'GB', icon: '../../../assets/icons/uk.svg', id: 'uk' },
    { label: 'Canada', value: 'CA', icon: '../../../assets/icons/canada.svg', id: 'canada' },
    { label: 'USA', value: 'US', icon: '../../../assets/icons/usa.svg', id: 'usa' },
    { label: 'Other', value: 'Other', icon: '../../../assets/icons/other.png', id: 'other' },
  ];
  genders = [
    { name: GenderDescriptions[Gender.Male], value: Gender.Male },
    { name: GenderDescriptions[Gender.Female], value: Gender.Female },
    { name: GenderDescriptions[Gender.Other], value: Gender.Other }
  ];

  courseLevels = [
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.BachelorsDegree], key: CourseLevelEnum.BachelorsDegree },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.MastersDegree], key: CourseLevelEnum.MastersDegree },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.DoctoralDegree], key: CourseLevelEnum.DoctoralDegree },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.Diploma], key: CourseLevelEnum.Diploma },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.Certificate], key: CourseLevelEnum.Certificate }
  ];
  journeryStatus = [
    { name: 'An Early Explorer', key: 'An Early Explorer', icon: '../../../assets/icons/telescope.png'},
    { name: 'Finalising Shortlist', key: 'Finalising Shortlist', icon: '../../../assets/icons/target.png' },
    { name: 'Ready-to-Apply', key: 'Ready-to-Apply', icon: '../../../assets/icons/touch-screen.png' }
  ];

  suggestions: any[] = [];
  selectedLatitude: number | null = null;
  selectedLongitude: number | null = null;

  collegeStartTimings: { name: string; key: string }[] = [];

  // Course levels that need parent info
  private readonly levelsNeedingParentInfo = [
    CourseLevelEnum.BachelorsDegree,
    CourseLevelEnum.BachelorsAndMastersDegree,
    CourseLevelEnum.Certificate,
    CourseLevelEnum.Diploma,
    CourseLevelEnum.FoundationDegree,
    CourseLevelEnum.UndergraduateCertificate,
    CourseLevelEnum.UndergraduateDiploma,
    CourseLevelEnum.Vocational
  ];

  loading = false;

  constructor(
    private formBuilder: FormBuilder,
    private utilityService: UtilityService,
    private accountCentreService: AccountCentreService,
    private messageService: MessageService,
    private authService: AuthService,
    private store: Store
  ) {
    this.initializePersonalInfoForm();
  }

  ngOnInit(): void {
    this.setCollegeStartTimings();
    this.prefillPersonalInfoForm();
  }

  searchLocation(event: any) {
    this.utilityService.getOLAPlaces(event.query).subscribe({
      next: (response) => {
        if (response && response.predictions) {
          this.suggestions = response.predictions;
        } else {
          this.suggestions = [];
        }
      },
      error: (err) => {
        // console.error('Error fetching location suggestions:', err);
        this.suggestions = [];
      }
    });
  }

  onLocationSelect(event: { value: Prediction }): void {
    const location = event?.value?.geometry?.location;
    this.selectedLatitude = location?.lat ?? null;
    this.selectedLongitude = location?.lng ?? null;
  }

  private initializePersonalInfoForm(): void {
    this.personalInfoForm = this.formBuilder.group({
      firstName: ['', Validators.required],
      lastName: [''],
      gender: [null, Validators.required],
      dateOfBirth: ['', Validators.required],
      intendedCountries: [[], Validators.required],
      intendedLevel: ['', Validators.required],
      collegeStartTiming: ['', Validators.required],
      journeyStatus:['', Validators.required],
      location: ['', Validators.required]
    });
  }

  // private setCollegeStartTimings(): void {
  //   const currentDate = new Date();
  //   const currentYear = currentDate.getFullYear();
  //   const currentMonth = currentDate.getMonth() + 1;
  //   const terms = ['Spring', 'Fall'] as const;

  //   const termEndMonths = {
  //     Spring: 7, // January (you can adjust this based on your definition)
  //     Fall: 12,   // August (you can adjust this as well)
  //   };

  //   for (let yearOffset = 0; yearOffset <= 3; yearOffset++) {
  //     const year = currentYear + yearOffset;
  //     for (const term of terms) {
  //       const termEndMonth = termEndMonths[term];

  //       // Skip terms that have already passed in the current year
  //       if (year === currentYear && termEndMonth < currentMonth) {
  //         continue;
  //       }
        
  //       const key = `${term} ${year}`;
  //       const name = `${term} ${year}`;
  //       this.collegeStartTimings.push({ name, key });
  //     }
  //   }
  // }
  private setCollegeStartTimings(): void {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // 1-based month
  
  this.collegeStartTimings = []; // Reset the array
  const maxYear = currentYear + 4;
  
  for (let year = currentYear; year <= maxYear; year++) {
    // Spring term deadline: October of PREVIOUS year
    const springDeadlineYear = year - 1;
    const springDeadlineMonth = 10; // October
    
    // Fall term deadline: March of SAME year
    const fallDeadlineYear = year;
    const fallDeadlineMonth = 3; // March
    
    // Check if Spring term is still valid to show
    const springDeadlinePassed = (currentYear > springDeadlineYear) || 
      (currentYear === springDeadlineYear && currentMonth > springDeadlineMonth);
    
    if (!springDeadlinePassed) {
      this.collegeStartTimings.push({ name: `Spring ${year}`, key: `Spring ${year}` });
    }
    
    // Check if Fall term is still valid to show
    const fallDeadlinePassed = (currentYear > fallDeadlineYear) || 
      (currentYear === fallDeadlineYear && currentMonth > fallDeadlineMonth);
    
    if (!fallDeadlinePassed) {
      this.collegeStartTimings.push({ name: `Fall ${year}`, key: `Fall ${year}` });
    }
  }
}

  public prefillPersonalInfoForm(): void {
    if (this.userId && this.userState === PersonalInfoState.StudentInfoPending) {
      this.authService.getUserByid(this.userId).subscribe({
        next: (user) => {
          this.personalInfoForm.patchValue({
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            gender: user.gender || null,
            dateOfBirth: user.bornOn ? new Date(user.bornOn) : null,
            intendedCountries: user.destination || [],
            intendedLevel: user.degreePreference !== null && user.degreePreference !== undefined ? user.degreePreference : '',
            collegeStartTiming: user.collageStartOn || '',
            journeyStatus: user.journeyStatus || '',
            location: user.location || ''
          });
          this.selectedLatitude = user.latitude ?? null;
          this.selectedLongitude = user.longitude ?? null;
        }
      });
    }
  }

  public updatePersonalInfo(): void {
    if (this.personalInfoForm.invalid) {
      this.personalInfoForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    const formData = this.personalInfoForm.value;
    const selectedCourseLevel = formData.intendedLevel;
    const selectedLocation = formData.location;
    const locationDescription = typeof selectedLocation === 'string'
      ? selectedLocation
      : selectedLocation?.description || null;

    if (selectedLocation && typeof selectedLocation === 'object' && selectedLocation?.geometry?.location) {
      this.selectedLatitude = selectedLocation.geometry.location.lat ?? null;
      this.selectedLongitude = selectedLocation.geometry.location.lng ?? null;
    }

    // Check if selected course level needs parent info
    const needsParentInfo = this.levelsNeedingParentInfo.includes(selectedCourseLevel);

    const dob: Date | null = formData.dateOfBirth;
    const dateOfBirth =
      dob instanceof Date
        ? `${dob.getFullYear()}-${String(dob.getMonth() + 1).padStart(2, '0')}-${String(dob.getDate()).padStart(2, '0')}`
        : null;

    const personBody: Record<string, unknown> = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      gender: formData.gender,
      dateOfBirth
    };

    const studentBody: Record<string, unknown> = {
      destination: formData.intendedCountries,
      // API expects a numeric (nullable) CourseLevelEnum, not a string.
      degreePreference:
        selectedCourseLevel === null || selectedCourseLevel === undefined || selectedCourseLevel === ''
          ? null
          : Number(selectedCourseLevel),
      collageStartOn: formData.collegeStartTiming,
      journeyStatus: formData.journeyStatus,
      location: locationDescription,
      latitude: this.selectedLatitude,
      longitude: this.selectedLongitude
    };

    forkJoin({
      person: this.accountCentreService.updateMyPerson(personBody),
      student: this.accountCentreService.updateMyStudentProfile(studentBody)
    }).subscribe({
      next: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Personal information updated successfully'
        });

        if (needsParentInfo) {
          // Update user state to ParentsInfoPending
          this.updateUserStateToParentsInfoPending();
          // Emit event to show parents info dialog
          this.showParentsInfo.emit();
        } else {
          this.updateSuccess.emit();
          this.closePersonalInfoDialog();
        }
      },
      error: (err) => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.message || 'Failed to update personal information'
        });
      }
    });
  }

  public closePersonalInfoDialog(): void {
    this.close.emit();
  }

  public resetPersonalInfo(): void {
    this.personalInfoForm.reset();
    this.prefillPersonalInfoForm();
  }

  private updateUserStateToParentsInfoPending(): void {
    // Get current user state from store (only once)
    this.store.select(UserSelectors.getUser).pipe(
      take(1)
    ).subscribe({
      next: (currentUserState) => {
        if (currentUserState && currentUserState.id) {
          // Dispatch UpdateUser action with updated personalInfoState
          this.store.dispatch(new UpdateUser({
            id: currentUserState.id,
            email: currentUserState.email,
            phoneNumber: currentUserState.phoneNumber,
            firstName: currentUserState.firstName,
            lastName: currentUserState.lastName,
            role: currentUserState.role,
            isNewUser: currentUserState.isNewUser,
            profileImageUrl: currentUserState.profileImageUrl,
            profileImageFormat: currentUserState.profileImageFormat,
            personalInfoState: PersonalInfoState.ParentsInfoPending
          }));
        }
      },
      error: (error) => {
        console.warn('Error fetching user state:', error);
      }
    });
  }
}
